import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Bell, Warning, CheckCircle, Prohibit, ArrowsClockwise,
  ChatCircle, Envelope, DeviceMobile, SlackLogo, MagnifyingGlass,
  ClockCounterClockwise, Stack, Lightning, ArrowUp, Package, Timer, ArrowRight,
  Question, ListChecks,
} from "@phosphor-icons/react";
import AlertFlowHelp from "./AlertFlowHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LIFECYCLE_STYLE = {
  open:         { bg: "bg-amber-500/15",   text: "text-amber-300",   ring: "ring-amber-700/40", label: "OPEN" },
  acknowledged: { bg: "bg-sky-500/15",     text: "text-sky-300",     ring: "ring-sky-700/40",   label: "ACKNOWLEDGED" },
  escalated:    { bg: "bg-red-500/15",     text: "text-red-300",     ring: "ring-red-700/50",   label: "ESCALATED" },
  resolved:     { bg: "bg-emerald-500/15", text: "text-emerald-300", ring: "ring-emerald-700/40", label: "RESOLVED" },
  dismissed:    { bg: "bg-zinc-500/15",    text: "text-zinc-400",    ring: "ring-zinc-700/40",  label: "DISMISSED" },
};

const SEVERITY_STYLE = {
  critical: { bg: "bg-red-500/15",    text: "text-red-300",    dot: "bg-red-500",    label: "CRITICAL" },
  warning:  { bg: "bg-amber-500/15",  text: "text-amber-300",  dot: "bg-amber-400",  label: "WARNING" },
  info:     { bg: "bg-sky-500/15",    text: "text-sky-300",    dot: "bg-sky-400",    label: "INFO" },
};

const CHANNEL_ICON = {
  in_app: Bell,
  email:  Envelope,
  sms:    DeviceMobile,
  slack:  SlackLogo,
};

const SOURCE_FILTERS = [
  { v: "",                    label: "Any source" },
  { v: "telemetry",           label: "Telemetry" },
  { v: "ingestion",           label: "Ingestion" },
  { v: "shipment_lifecycle",  label: "Shipment lifecycle" },
  { v: "corrective_action",   label: "Corrective action" },
  { v: "sweep",               label: "Overdue sweep" },
  { v: "ai_anomaly",          label: "AI anomaly" },
  { v: "manual",              label: "Manual" },
];

const LIFECYCLE_FILTERS = [
  { v: "active",        label: "Active" },
  { v: "open",          label: "Open" },
  { v: "acknowledged",  label: "Acknowledged" },
  { v: "escalated",     label: "Escalated" },
  { v: "resolved",      label: "Resolved" },
  { v: "dismissed",     label: "Dismissed" },
  { v: "",              label: "All" },
];

const SEVERITY_FILTERS = [
  { v: "",         label: "All severities" },
  { v: "critical", label: "Critical only" },
  { v: "warning",  label: "Warning+" },
  { v: "info",     label: "Info+" },
];

const CHANNEL_FILTERS = [
  { v: "",        label: "Any channel" },
  { v: "in_app",  label: "In-app" },
  { v: "email",   label: "Email" },
  { v: "sms",     label: "SMS" },
  { v: "slack",   label: "Slack" },
];

function ChannelChip({ channel }) {
  const Icon = CHANNEL_ICON[channel] || Bell;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-zinc-900/70 border border-zinc-800 text-zinc-300"
      data-testid={`channel-chip-${channel}`}>
      <Icon size={9} weight="duotone" /> {channel.replace("_", "-")}
    </span>
  );
}

function TotalsStrip({ totals, loading, onRefresh, generatedAt }) {
  const tiles = [
    { label: "Open",         value: totals.open || 0,         tone: "text-amber-300",   icon: Warning },
    { label: "Acknowledged", value: totals.acknowledged || 0, tone: "text-sky-300",     icon: CheckCircle },
    { label: "Escalated",    value: totals.escalated || 0,    tone: "text-red-300",     icon: ArrowUp },
    { label: "Resolved",     value: totals.resolved || 0,     tone: "text-emerald-300", icon: CheckCircle },
    { label: "Critical",     value: totals.critical || 0,     tone: "text-red-300",     icon: Lightning },
    { label: "Warning",      value: totals.warning || 0,      tone: "text-amber-300",   icon: Warning },
    { label: "Email sent",   value: totals.channel_email || 0,tone: "text-zinc-300",    icon: Envelope },
    { label: "SMS sent",     value: totals.channel_sms || 0,  tone: "text-zinc-300",    icon: DeviceMobile },
  ];
  return (
    <div className="flex items-center justify-between gap-4 mb-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 flex-1" data-testid="alert-totals">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="px-3 py-2 rounded border border-zinc-800 bg-zinc-950/40">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500">
                <Icon size={10} weight="duotone" />
                {t.label}
              </div>
              <div className={`text-lg font-mono font-bold mt-0.5 ${t.tone}`}>{t.value}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        {generatedAt && (
          <span className="text-[10px] font-mono text-zinc-500">
            {new Date(generatedAt).toUTCString().slice(17, 25)} UTC
          </span>
        )}
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs h-8"
          data-testid="alert-center-refresh">
          <ArrowsClockwise size={12} weight="duotone" className={loading ? "animate-spin mr-1" : "mr-1"} />
          Refresh
        </Button>
      </div>
    </div>
  );
}

function LifecycleBar({ alert, onAct }) {
  const status = alert.lifecycle_status || "open";
  if (["resolved", "dismissed"].includes(status)) return null;

  const actions = [];
  if (status === "open" || status === "escalated") {
    actions.push({ op: "acknowledge", label: "Acknowledge", style: "bg-sky-500 hover:bg-sky-600 text-zinc-950" });
  }
  if (status === "open" || status === "acknowledged") {
    actions.push({ op: "escalate", label: "Escalate", style: "border-red-700/60 text-red-300 hover:bg-red-500/10", outline: true });
  }
  actions.push({ op: "resolve", label: "Resolve", style: "bg-emerald-500 hover:bg-emerald-600 text-zinc-950" });
  actions.push({ op: "dismiss", label: "Dismiss", style: "border-zinc-700 text-zinc-400 hover:bg-zinc-800", outline: true });

  return (
    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
      {actions.map((a) => (
        <Button key={a.op} size="sm" variant={a.outline ? "outline" : "default"}
          onClick={(e) => { e.stopPropagation(); onAct(alert.id, a.op); }}
          className={`text-[10px] h-6 px-2 ${a.style}`}
          data-testid={`alert-${a.op}-${alert.id}`}>
          {a.label}
        </Button>
      ))}
    </div>
  );
}

function SlaCountdown({ expiresAt, lifecycle, escalationStep }) {
  if (!expiresAt) return null;
  if (["resolved", "dismissed"].includes(lifecycle)) return null;
  const diffSec = (new Date(expiresAt).getTime() - Date.now()) / 1000;
  const overdue = diffSec < 0;
  const abs = Math.abs(diffSec);
  let label;
  if (abs < 60) label = `${Math.floor(abs)}s`;
  else if (abs < 3600) label = `${Math.floor(abs / 60)}m`;
  else if (abs < 86400) label = `${(abs / 3600).toFixed(1)}h`;
  else label = `${Math.floor(abs / 86400)}d`;
  const cls = overdue
    ? "border-red-700/60 bg-red-500/10 text-red-300"
    : diffSec < 300
      ? "border-amber-700/60 bg-amber-500/10 text-amber-300"
      : "border-zinc-700 bg-zinc-900/60 text-zinc-400";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${cls}`}
      title={`SLA window ${overdue ? "exceeded" : "expires"} at ${new Date(expiresAt).toUTCString().slice(5, 22)}`}
      data-testid="sla-countdown">
      <Timer size={10} weight="duotone" />
      {overdue ? `SLA -${label}` : `SLA ${label}`}
    </span>
  );
}

function EscalationLevelPill({ step, terminal }) {
  if (!step) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${
      terminal
        ? "border-red-700/60 bg-red-500/10 text-red-300"
        : "border-amber-700/60 bg-amber-500/10 text-amber-300"
    }`}
      data-testid="escalation-level"
      title={terminal ? "All escalation steps exhausted" : `Currently at escalation step ${step}`}>
      <ArrowUp size={10} weight="bold" />
      L{step}{terminal ? " ◉" : ""}
    </span>
  );
}

function DispatchStatusPill({ status, reason }) {
  // Map the backend taxonomy → operator-friendly label + tone.
  // Renders nothing for "unknown" (pre-Phase50 alerts) and "dispatched" (positive
  // signal is already conveyed by the green channel chips below the row).
  if (!status || status === "unknown" || status === "dispatched") return null;
  const STYLE = {
    no_rule_configured:     { bg: "bg-zinc-800/70",  text: "text-zinc-300",  border: "border-zinc-700",   label: "No Rule" },
    no_recipients_resolved: { bg: "bg-red-500/15",   text: "text-red-300",   border: "border-red-500/30", label: "No Recipients" },
    held_lifecycle:         { bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/30", label: "Held — Not Active" },
    below_severity_min:     { bg: "bg-zinc-800/70",  text: "text-zinc-400",  border: "border-zinc-700",   label: "Below Threshold" },
    facility_filter_no_match: { bg: "bg-zinc-800/70", text: "text-zinc-400", border: "border-zinc-700",   label: "Filtered Out" },
    in_app_only:            { bg: "bg-teal-500/10",  text: "text-teal-300",  border: "border-teal-500/30", label: "In-App Only" },
  };
  const s = STYLE[status] || STYLE.no_rule_configured;
  return (
    <span
      className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${s.bg} ${s.text} ${s.border}`}
      title={reason || s.label}
      data-testid={`dispatch-status-${status}`}>
      {s.label}
    </span>
  );
}

function AlertRow({ alert, onAct, onOpen }) {
  const lf = LIFECYCLE_STYLE[alert.lifecycle_status || "open"] || LIFECYCLE_STYLE.open;
  const sev = SEVERITY_STYLE[(alert.severity || "info").toLowerCase()] || SEVERITY_STYLE.info;
  const channels = alert.channels_dispatched || [];
  return (
    <div
      className={`p-3 rounded border border-zinc-800 bg-zinc-900/30 ring-1 ${lf.ring} hover:bg-zinc-900/60 transition-colors cursor-pointer`}
      onClick={() => onOpen(alert)}
      data-testid={`alert-row-${alert.id}`}>
      <div className="flex items-start gap-3">
        <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${sev.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${sev.bg} ${sev.text}`}>
              {sev.label}
            </span>
            <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${lf.bg} ${lf.text}`}>
              {lf.label}
            </span>
            <DispatchStatusPill status={alert.dispatch_status} reason={alert.dispatch_reason} />
            <EscalationLevelPill step={alert.current_escalation_step} terminal={alert.escalation_terminal} />
            <SlaCountdown
              expiresAt={alert.next_sla_expiry || alert.expires_at}
              lifecycle={alert.lifecycle_status}
              escalationStep={alert.current_escalation_step}
            />
            {alert.alert_type && (
              <span className="text-[10px] text-zinc-500 font-mono">{alert.alert_type}</span>
            )}
            {alert.source && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800/80 text-zinc-400 border border-zinc-700"
                title={`Emitted by ${alert.source}`} data-testid={`alert-source-${alert.source}`}>
                {alert.source.replace(/_/g, " ")}
              </span>
            )}
            <span className="text-[10px] text-zinc-500 ml-auto font-mono">
              {alert.created_at ? new Date(alert.created_at).toUTCString().slice(5, 22) : "—"}
            </span>
          </div>
          <div className="text-sm text-zinc-100 mt-1">{alert.message || "(no message)"}</div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[10px] text-zinc-500">
            {alert.device_name && <span>Device: <span className="text-zinc-300">{alert.device_name}</span></span>}
            {alert.shipment_id && <span className="flex items-center gap-1"><Package size={9} weight="duotone" />{(alert.shipment_id || "").slice(0, 8)}</span>}
            {alert.assigned_to_label && (
              <span>Assignee: <span className="text-zinc-300">{alert.assigned_to_label}</span></span>
            )}
          </div>
          {channels.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Dispatched:</span>
              {channels.map((c) => <ChannelChip key={c} channel={c} />)}
            </div>
          )}
          {alert.dispatch_status && alert.dispatch_status !== "dispatched" &&
           alert.dispatch_status !== "unknown" && alert.dispatch_reason && (
            <div className="mt-1.5 text-[10px] text-zinc-500 italic leading-relaxed flex items-start gap-1"
              data-testid={`dispatch-reason-${alert.id}`}>
              <span className="text-zinc-600 mt-0.5">›</span>
              <span>{alert.dispatch_reason}</span>
            </div>
          )}
          <LifecycleBar alert={alert} onAct={onAct} />
        </div>
      </div>
    </div>
  );
}

function AlertDetailDrawer({ alert, onClose, onAct }) {
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!alert) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [detailRes, timelineRes] = await Promise.all([
          axios.get(`${API}/alert-center/${alert.id}`),
          axios.get(`${API}/alert-center/${alert.id}/timeline`),
        ]);
        if (!cancelled) {
          setDetail(detailRes.data);
          setTimeline(timelineRes.data);
        }
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [alert]);

  if (!alert) return null;
  const a = detail?.alert || alert;
  const lf = LIFECYCLE_STYLE[a.lifecycle_status || "open"];
  const sev = SEVERITY_STYLE[(a.severity || "info").toLowerCase()];

  return (
    <div className="fixed inset-0 bg-black/60 z-[1900] flex justify-end" onClick={onClose}>
      <div className="w-full md:w-[680px] bg-zinc-950 border-l border-zinc-800 h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="alert-detail-drawer">
        <div className="p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-2">
                <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${sev.bg} ${sev.text}`}>{sev.label}</span>
                <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${lf.bg} ${lf.text}`}>{lf.label}</span>
                <span className="text-[10px] text-zinc-500 font-mono">{a.alert_type}</span>
              </div>
              <h2 className="text-sm font-semibold text-zinc-100">{a.message}</h2>
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                {a.created_at ? new Date(a.created_at).toUTCString().slice(5) : ""}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-500">Close</Button>
          </div>
          <LifecycleBar alert={a} onAct={onAct} />
        </div>

        <div className="p-5 space-y-5">
          {a.assigned_to_label && (
            <section>
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Assignee</h3>
              <div className="text-sm text-zinc-100">{a.assigned_to_label}</div>
              <div className="text-[10px] text-zinc-500">{a.assigned_to_party_type}</div>
            </section>
          )}

          {/* Unified timeline (created → primary routed → escalations → ack/resolve) */}
          <section data-testid="alert-timeline">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500">Timeline</h3>
              {timeline?.next_sla_expiry && !["resolved", "dismissed"].includes(a.lifecycle_status) && (
                <SlaCountdown
                  expiresAt={timeline.next_sla_expiry}
                  lifecycle={a.lifecycle_status}
                  escalationStep={timeline.current_escalation_step}
                />
              )}
            </div>
            {timeline?.events?.length > 0 ? (
              <ol className="relative ml-2 pl-4 border-l border-zinc-800 space-y-2">
                {timeline.events.map((ev, idx) => {
                  const isEsc = (ev.kind || "").startsWith("escalat");
                  const isCreated = ev.kind === "created";
                  const isAck = ev.kind === "alert_acknowledged";
                  const isResolved = ev.kind === "alert_resolved";
                  const isDismissed = ev.kind === "alert_dismissed";
                  const isSlaTerminal = ev.kind === "alert_escalated_terminal_by_sla";
                  const isPrimary = ev.kind === "primary_routed";
                  const isSkipped = ev.kind === "alert_escalation_skipped_no_recipients";
                  const dotColor =
                    isResolved ? "bg-emerald-500" :
                    isDismissed ? "bg-zinc-500" :
                    isAck ? "bg-sky-500" :
                    isSlaTerminal ? "bg-red-600" :
                    isEsc ? "bg-amber-500" :
                    isSkipped ? "bg-zinc-600" :
                    isPrimary ? "bg-teal-500" :
                    isCreated ? "bg-zinc-300" :
                    "bg-zinc-500";
                  return (
                    <li key={idx} className="relative" data-testid={`tl-event-${ev.kind}`}>
                      <span className={`absolute -left-[1.06rem] top-1.5 w-2 h-2 rounded-full ${dotColor}`} />
                      <div className="text-xs text-zinc-100">{ev.label}</div>
                      <div className="text-[10px] text-zinc-500 font-mono">
                        {ev.timestamp ? new Date(ev.timestamp).toUTCString().slice(5, 22) : "—"}
                        {ev.user_email ? ` · ${ev.user_email}` : ""}
                      </div>
                      {ev.details?.channels && ev.details.channels.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {ev.details.channels.map((c) => <ChannelChip key={c} channel={c} />)}
                        </div>
                      )}
                      {ev.details?.recipients && ev.details.recipients.length > 0 && (
                        <div className="text-[10px] text-zinc-500 mt-1">
                          → {ev.details.recipients.slice(0, 3).map((r) =>
                            r.contact_name || r.role
                          ).join(", ")}
                          {ev.details.recipients.length > 3 ? ` +${ev.details.recipients.length - 3} more` : ""}
                        </div>
                      )}
                      {isSkipped && (
                        <div className="text-[10px] text-zinc-500 mt-0.5 italic">
                          (no contact registered for {(ev.details?.route_to || []).join("/")} — cascaded)
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-[11px] italic text-zinc-500">No timeline events recorded.</p>
            )}
          </section>

          <section data-testid="alert-dispatch-trail">
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Channel dispatch</h3>
            {detail?.dispatch?.length > 0 ? (
              <div className="space-y-1.5">
                {detail.dispatch.map((d) => (
                  <div key={d.id} className="p-2 rounded border border-zinc-800 bg-zinc-900/40 flex items-center gap-2">
                    <ChannelChip channel={d.channel || "in_app"} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-100">{d.contact_name || d.role}</div>
                      <div className="text-[10px] text-zinc-500">
                        {d.role}{d.contact_email ? ` · ${d.contact_email}` : ""}{d.contact_phone ? ` · ${d.contact_phone}` : ""}
                      </div>
                    </div>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                      d.status === "sent" ? "bg-emerald-500/15 text-emerald-300" :
                      d.status === "scheduled" ? "bg-amber-500/15 text-amber-300" :
                      d.status === "escalated" ? "bg-red-500/15 text-red-300" :
                      d.status === "cancelled" ? "bg-zinc-500/15 text-zinc-400" :
                      "bg-sky-500/15 text-sky-300"
                    }`}>
                      {d.status}
                    </span>
                    {d.delivery_status === "mock_dispatched" && (
                      <span className="text-[9px] uppercase font-bold text-zinc-500" title="Channel integration not yet wired — logged for audit">MOCK</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] italic text-zinc-500">No dispatch records (alert was created before routing was hooked).</p>
            )}
          </section>

          <section data-testid="alert-audit-trail">
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Audit trail</h3>
            {(a.audit_trail || []).length > 0 ? (
              <div className="space-y-1">
                {(a.audit_trail || []).map((e, i) => (
                  <div key={i} className="text-[11px] p-2 rounded border border-zinc-800 bg-zinc-900/30">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-zinc-100 font-medium">{e.action}</span>
                      <span className="font-mono text-zinc-500">{e.timestamp ? new Date(e.timestamp).toUTCString().slice(5, 22) : ""}</span>
                    </div>
                    <div className="text-zinc-500">{e.user_email}</div>
                    {e.details?.note && <div className="text-zinc-400 italic mt-0.5">"{e.details.note}"</div>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] italic text-zinc-500">No lifecycle activity yet.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/* -------- Self-test result modal (admin-only) ------------------------------ */
function SelfTestResultModal({ result, onClose, onJumpToAlert }) {
  const delivered = result?.delivered === true || result?.status === "delivered";
  const mocked = result?.status === "mock_dispatched";
  const failed = !delivered && !mocked;

  const titleClr = delivered ? "text-emerald-300" : mocked ? "text-amber-300" : "text-red-300";
  const ringClr  = delivered ? "ring-emerald-700/40" : mocked ? "ring-amber-700/40" : "ring-red-700/40";
  const Icon     = delivered ? CheckCircle : mocked ? Warning : Prohibit;
  const verb     = delivered ? "DELIVERED" : mocked ? "MOCKED" : "FAILED";

  return (
    <div
      className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="self-test-result-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-[480px] max-w-[92vw] ring-1 ${ringClr}`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Icon size={18} weight="duotone" className={titleClr} />
            <h2 className="text-sm font-semibold text-zinc-100">Test alert · {verb}</h2>
            <span className="text-[9px] uppercase tracking-wider font-bold text-teal-300 border border-teal-700/50 bg-teal-500/10 rounded px-1.5 py-0.5">
              IS_TEST
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-300 text-lg leading-none"
            data-testid="self-test-modal-close"
          >×</button>
        </div>

        <div className="px-4 py-3 space-y-2 text-xs">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-zinc-500">Recipient</span>
            <span className="text-zinc-200 font-mono" data-testid="self-test-recipient">{result.recipient_email || "—"}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-zinc-500">Status</span>
            <span className={`font-semibold ${titleClr}`} data-testid="self-test-status">{result.status || "—"}</span>
          </div>
          {result.provider_id && (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-zinc-500">Provider ID</span>
              <span className="text-zinc-300 font-mono text-[11px] truncate max-w-[260px]" title={result.provider_id} data-testid="self-test-provider-id">
                {result.provider_id}
              </span>
            </div>
          )}
          {result.error && (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-zinc-500">Error</span>
              <span className="text-red-300 text-[11px] truncate max-w-[260px]" title={result.error}>{result.error}</span>
            </div>
          )}
          {result.shipment_name && (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-zinc-500">Test shipment context</span>
              <span className="text-zinc-300 truncate max-w-[260px]" title={result.shipment_name}>{result.shipment_name}</span>
            </div>
          )}
          {result.alert_id && (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-zinc-500">Alert ID</span>
              <span className="text-zinc-300 font-mono text-[11px]">{result.alert_id.slice(0, 8)}…</span>
            </div>
          )}
          {mocked && (
            <p className="text-amber-300 text-[11px] pt-1">
              SendGrid is not yet configured for this tenant. The alert and audit row were
              written, but no email was actually sent.
            </p>
          )}
          {delivered && (
            <p className="text-emerald-300 text-[11px] pt-1">
              Check your inbox. The subject is prefixed with{" "}
              <span className="font-mono">[TEST ALERT — No operational action required]</span>{" "}
              so it cannot be confused with a real critical event.
            </p>
          )}
          <div className="mt-2 text-[10px] text-zinc-500 leading-relaxed border-t border-zinc-800 pt-2">
            Tagged <span className="font-mono text-teal-400">is_test=true</span>. Excluded from
            evidence-package verdicts and SLA breach metrics. Does not create a corrective
            action. Audit + dispatch rows persisted.
          </div>
        </div>

        <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-end gap-2">
          {result.alert_id && onJumpToAlert && (
            <button
              onClick={() => onJumpToAlert(result.alert_id)}
              className="text-[11px] px-3 h-7 rounded border border-teal-700/60 text-teal-300 hover:bg-teal-500/10"
              data-testid="self-test-jump-to-alert"
            >
              View in Alert Center →
            </button>
          )}
          <button
            onClick={onClose}
            className="text-[11px] px-3 h-7 rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="self-test-modal-dismiss"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AlertCenter({ isAdmin = false }) {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [lifecycle, setLifecycle] = useState("active");
  const [severity, setSeverity] = useState("");
  const [channel, setChannel] = useState("");
  const [source, setSource] = useState("");
  const [query, setQuery] = useState("");
  const [drawerAlert, setDrawerAlert] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/alert-center`, {
        params: { lifecycle: lifecycle || undefined, severity: severity || undefined, channel: channel || undefined, source: source || undefined, q: query || undefined, limit: 300 },
      });
      setItems(data.items || []);
      setTotals(data.totals || {});
      setGeneratedAt(data.generated_at);
    } catch (e) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, [lifecycle, severity, channel, source, query]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleAct = async (alertId, op) => {
    let note;
    if (op === "dismiss") {
      note = window.prompt("Reason for dismissing this alert? (optional)") || undefined;
    }
    try {
      await axios.patch(`${API}/alert-center/${alertId}/lifecycle`, { op, note });
      toast.success(`Alert ${op}d`);
      fetchAlerts();
      if (drawerAlert?.id === alertId) {
        // Refresh drawer detail
        try {
          const { data } = await axios.get(`${API}/alert-center/${alertId}`);
          setDrawerAlert(data.alert);
        } catch { /* noop */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${op}`);
    }
  };

  const handleSweep = async () => {
    setSweeping(true);
    const t = toast.loading("Sweeping overdue corrective actions…");
    try {
      const { data } = await axios.post(`${API}/alert-center/sweep/overdue-corrective-actions`);
      toast.success(`Emitted ${data.emitted} overdue alerts (${data.routed} routed)`, { id: t });
      fetchAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed", { id: t });
    } finally {
      setSweeping(false);
    }
  };

  // Self-test alert state — admin button + result modal so the operator can
  // verify SendGrid is actually delivering before a real critical event.
  const [selfTesting, setSelfTesting] = useState(false);
  const [selfTestResult, setSelfTestResult] = useState(null);

  const handleSelfTest = async () => {
    setSelfTesting(true);
    const t = toast.loading("Sending test alert email to your inbox…");
    try {
      const { data } = await axios.post(`${API}/alert-center/self-test`);
      setSelfTestResult(data);
      if (data.delivered) {
        toast.success(`Test email delivered to ${data.recipient_email}`, { id: t });
      } else if (data.status === "mock_dispatched") {
        toast.warning("SendGrid not configured — dispatch was mocked", { id: t });
      } else {
        toast.error(data.error || "Dispatch failed", { id: t });
      }
      fetchAlerts();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Self-test failed";
      toast.error(detail, { id: t });
      setSelfTestResult({ ok: false, status: "failed", error: detail });
    } finally {
      setSelfTesting(false);
    }
  };

  const grouped = useMemo(() => items, [items]);

  return (
    <div className="max-w-[1600px] mx-auto" data-testid="alert-center">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2">
            <Bell size={18} weight="duotone" className="text-amber-400" />
            Alert Center
            <span
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-amber-300 border border-amber-700/50 bg-amber-500/10 rounded px-1.5 py-0.5 ml-1"
              title="Operator work queue. Acknowledge / resolve / dismiss / manually escalate alerts produced by Event Orchestration."
              data-testid="alert-center-role-badge"
            >
              <ListChecks size={9} weight="duotone" /> cockpit · operator queue
            </span>
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Lifecycle queue for alerts produced by{" "}
            <span className="text-teal-300">Event Orchestration</span>. Acknowledge, resolve, dismiss, or
            manually escalate. Outbound dispatch is owned by Orchestration.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHelpOpen(true)}
            className="h-8 text-[11px] border-amber-700/50 text-amber-300 hover:bg-amber-500/10"
            data-testid="alert-center-flow-help-btn"
          >
            <Question size={12} className="mr-1" weight="duotone" /> How alerts flow
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleSelfTest} disabled={selfTesting}
              className="border-teal-700/60 text-teal-300 hover:bg-teal-500/10 text-xs h-8"
              data-testid="self-test-alert-btn"
              title="Emit a synthetic test alert and email it to your address — verifies SendGrid is delivering.">
              <Envelope size={12} className={selfTesting ? "animate-pulse mr-1" : "mr-1"} weight="duotone" />
              {selfTesting ? "Sending…" : "Send myself a test alert"}
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleSweep} disabled={sweeping}
              className="border-amber-700/60 text-amber-300 hover:bg-amber-500/10 text-xs h-8"
              data-testid="sweep-cas-btn">
              <ClockCounterClockwise size={12} className={sweeping ? "animate-spin mr-1" : "mr-1"} />
              Sweep Overdue CAs
            </Button>
          )}
        </div>
      </div>

      {/* Self-test result modal */}
      {selfTestResult && (
        <SelfTestResultModal
          result={selfTestResult}
          onClose={() => setSelfTestResult(null)}
          onJumpToAlert={(alertId) => {
            setSelfTestResult(null);
            // Surface the alert by switching to All filter and searching its id.
            setLifecycle("");
            setQuery(alertId.slice(0, 8));
          }}
        />
      )}

      <TotalsStrip totals={totals} loading={loading} onRefresh={fetchAlerts} generatedAt={generatedAt} />

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="alert-filters">
        <div className="flex items-center gap-1 p-0.5 bg-zinc-900 rounded border border-zinc-800">
          {LIFECYCLE_FILTERS.map((f) => (
            <button key={f.v} onClick={() => setLifecycle(f.v)} type="button"
              className={`text-xs h-7 px-2.5 rounded ${lifecycle === f.v ? "bg-amber-500/15 text-amber-300 font-semibold" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`lf-filter-${f.v || "all"}`}>
              {f.label}
            </button>
          ))}
        </div>

        <select value={severity} onChange={(e) => setSeverity(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-200 px-2.5 h-8"
          data-testid="severity-filter">
          {SEVERITY_FILTERS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>

        <select value={channel} onChange={(e) => setChannel(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-200 px-2.5 h-8"
          data-testid="channel-filter">
          {CHANNEL_FILTERS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>

        <select value={source} onChange={(e) => setSource(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-200 px-2.5 h-8"
          data-testid="source-filter">
          {SOURCE_FILTERS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>

        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search message, device…"
            className="bg-zinc-900 border-zinc-800 text-xs h-8 pl-7 w-60"
            data-testid="alert-search" />
        </div>

        <span className="text-[10px] font-mono text-zinc-500 ml-auto">
          {grouped.length} alerts shown
        </span>
      </div>

      {grouped.length === 0 ? (
        <Card className="bg-zinc-900/30 border-zinc-800 p-12 text-center">
          <CheckCircle size={20} weight="duotone" className="text-emerald-400 mx-auto mb-2" />
          <p className="text-sm text-zinc-300">
            {loading ? "Loading…" : "No alerts match the current filters."}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="alert-list">
          {grouped.map((a) => (
            <AlertRow key={a.id} alert={a} onAct={handleAct} onOpen={setDrawerAlert} />
          ))}
        </div>
      )}

      <AlertDetailDrawer
        alert={drawerAlert}
        onClose={() => setDrawerAlert(null)}
        onAct={handleAct}
      />

      <AlertFlowHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
